import { Box, Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  DatePicker,
  PageTitleAndSubTitle,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import moment from 'moment';
import { getAvatarComponent } from '../../../core-utils/helper-utils/TaskBoardHelper';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import { CheckBox } from '@HomaHealth/ui-component-library';
import { useStores } from '../../../stores';
import {
  getRiskIdChip,
  removeEmptyFields,
} from '../../../core-utils/helper-utils/riskRegisterHelper';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { getTextWithToolTip } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import taskBoardConstants from '../../../core-utils/messages/TaskBoardConstants';
import accessDashboardConstants from '../../../core-utils/messages/AccessDashboardConstants';

export interface AddActivityModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
   
  handleSubmit: (data: any) => void;
  handleOpenSnackBar: any;
  eg_nid: string;
}

export interface ActivityData {
  start_date: string;
  name: string;
  owner: string[];
  tracker: string;
  activity_outcome: string;
}

const AddActivityModal: React.FC<AddActivityModalProps> = observer(
  ({ open, setOpen, handleSubmit, eg_nid }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '676px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginRight: '25px',
        width: '100%',
      },
      activity: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 0 0px 20px',
      },
      infoItem: {
        boxSizing: 'border-box',
        width: '586px',
        height: '100px',
        background: '#E1A9481A',
        border: '0px',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '20px',
      },
    });
    const { classes } = useStyles();

    //States
    const [data, setData] = useState<any>({
      name: '',
      description: '',
      primary_category: '',
      connected_to: [],
      start_date: '',
      suggested_start_date: '',
      owner: [],
      tracker: '',
      impact: [],
      risk_source: '',
      activity_status: '',
      source_report: '',
      contract_id: '',
    });
    const [sourceOptions, setSourceOptions] = useState<any>([]);
    const [sourceValues, setSourceValues] = useState<any>([]);
    const [contracts, setContracts] = useState<any>([]);
    const [ownerOptions, setOwnerOptions] = useState<any>([]);
    const [trackerOptions, setTrackerOptions] = useState<any>([]);
    const [trackerValues, setTrackerValues] = useState<any>([]);
    const [connectedToOptions, setConnectedToOptions] = useState<any>([]);
    const [connectedToValues, setConnectedToValues] = useState<any>([]);

    //Constants
    const isCommonFieldsFilled =
      data.name &&
      data.primary_category &&
      data.impact &&
      data.owner &&
      (data.start_date || data.suggested_start_date);

    const isAddActivityDisabled = !isCommonFieldsFilled;
    const userDetailsStore = useStores().userDetailsStore;

    //functions
    const handleClose = () => {
      setOpen(false);
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      setData((prevData: any) => ({
        ...prevData,
        [fieldName.toLowerCase()]: value,
      }));
    };

    //Apis
    const getSourceOptions = async () => {
      const contracts = await axios.post(
        properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.getContracts,
        {
          eg_nid,
          //   eg_nid: 'denali_water solutions_1733163850381',
        },
      );
      const allContracts = contracts.data.data.map((item: any) => {
        return {
          name: `${item.health_care_partner_name} - ${item.service_agreement_name}`,
          reports: item.reports,
          contract_id: item.contract_id,
        };
      });
      setContracts(allContracts);
      const accessibleScreens = userDetailsStore.allScreens.riskManagement.map(
        (screen) => screen.name,
      );
      const options = [];
      const values = [];
      if (accessibleScreens.includes('Summary')) {
        options.push('Summary');
        values.push('Summary');
      }
      const individualScreens = accessDashboardConstants.DASHBOARD_CARDS.filter(
        (card: any) => accessibleScreens.includes(card.category),
         
      ).flatMap((card: any) => card.subGroups.map((sub: any) => sub.group));
      options.push(
        ...individualScreens.map((item) => getTextWithToolTip(item, 30)),
        ...allContracts.map((item: any) => getTextWithToolTip(item.name, 30)),
      );
      values.push(
        ...individualScreens,
        ...allContracts.map((item: any) => item.name),
      );
      setSourceValues(values);
      setSourceOptions(options);
      const ownerOptions = contracts.data.data.map((item: any) => {
        return `${item.service_agreement_name} - ${item.health_care_partner_name}`;
      });
      setOwnerOptions(ownerOptions);
    };

    const getTrackersList = async () => {
      const roles = ['ADMIN', 'USER'];
      const requests = roles.map((role) =>
        axios.get(
          `${properties.userManagementBaseUrl}${properties.users}${properties.getByRole}/${role}`,
        ),
      );

      const responses = await Promise.all(requests);

      const allUsers = responses
        .flatMap((res) => res.data)
        .filter((user: any) =>
          user.employerGroups?.some((group: any) => group.egNid === eg_nid),
        );
      const options = allUsers.map((item: any) =>
        getAvatarComponent(item.email, item.firstName, item.lastName),
      );
      const values = allUsers.map((item: any) => item.email);
      setTrackerOptions(options);
      setTrackerValues(values);
    };

    const getRisks = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.getRisk,
          {
            // eg_nid: 'crane_nxt_1742408999728',
            eg_nid: eg_nid,
            status: ['New', 'Planned', 'In Progress'],
          },
        )
        .then((res: any) => {
          const values: any = [];
          const label: any = [];
          res.data.data.risks.map((item: any) => {
            values.push(item.risk_id);
            label.push(
              <div
                style={{ display: 'flex', gap: '3px', alignItems: 'center' }}
              >
                {getRiskIdChip(item.risk_id)}{' '}
                {getTextWithToolTip(item.title, 15)}
              </div>,
            );
          });
          setConnectedToOptions(label);
          setConnectedToValues(values);
        });
    };

    useEffect(() => {
      getRisks();
      getSourceOptions();
      getTrackersList();
    }, []);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={taskBoardConstants.ADD_TASK}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid
              className={classes.scroll}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                overflowY: 'scroll',
                height: '570px',
                paddingBottom: '250px',
              }}
            >
              <ReportCreateNewLabelField
                label={taskBoardConstants.ACT_TITLE}
                component={
                  <TextField
                    width="580px"
                    height="46px"
                    placeholder={taskBoardConstants.ACT_TITLE}
                    onChange={(event: any) => handleChange(event, 'name')}
                    value={data.name}
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                    error={!data.name}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={taskBoardConstants.DESCRIPTION}
                component={
                  <TextField
                    width="580px"
                    maxRows={3}
                    multiline={true}
                    placeholder={taskBoardConstants.DESCRIPTION}
                    onChange={(event: any) =>
                      handleChange(event, 'description')
                    }
                    value={data.description}
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={taskBoardConstants.ACT_CATEGORY}
                component={
                  <Dropdown
                    label={taskBoardConstants.ACT_CATEGORY}
                    dropDownOptions={taskBoardConstants.CATEGORY_OPTIONS.map(
                      (item: any) => {
                        return getTextWithToolTip(item, 25);
                      },
                    )}
                    dropDownValues={taskBoardConstants.CATEGORY_OPTIONS}
                    value={data.primary_category}
                    setValue={(value: string) =>
                      setData((prevData: any) => ({
                        ...prevData,
                        primary_category: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) => {
                      const contract_id =
                        contracts?.find(
                          (item: any) => item.name === event.target.value,
                        )?.contract_id ?? '';
                      setData((prevData: any) => ({
                        ...prevData,
                        primary_category: event.target.value,
                        contract_id: contract_id,
                      }));
                    }}
                    error={!data.primary_category}
                  />
                }
              />

              <ReportCreateNewLabelField
                label={taskBoardConstants.IMPACT}
                component={
                  <AutocompleteWithCheckBoxes
                    options={taskBoardConstants.IMPACT_VALUES}
                    defaultValue={data.impact}
                    handleChange={(_, value) =>
                      setData((prev: any) => ({ ...prev, impact: value }))
                    }
                    getOptionLabel={(option: any) => option}
                    placeholder={taskBoardConstants.IMPACT_PLACEHOLDER}
                    width="283px"
                     
                    renderOption={(option: any, { selected }: any) => {
                      const index = taskBoardConstants.IMPACT_VALUES.indexOf(
                        option,
                      );
                      return (
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                          {taskBoardConstants.IMPACT_OPTIONS[index]}
                        </Box>
                      );
                    }}
                    noOptionsText="No Impact Type Found"
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={taskBoardConstants.CONNECT}
                component={
                  <AutocompleteWithCheckBoxes
                    options={connectedToValues}
                    defaultValue={data.connected_to}
                    handleChange={(_, value) =>
                      setData((prev: any) => ({ ...prev, connected_to: value }))
                    }
                    getOptionLabel={(option: any) => option}
                    placeholder={taskBoardConstants.CONNECT}
                    width="300px"
                    renderOption={(option: any, { selected }: any) => {
                      const index = connectedToValues.indexOf(option);
                      return (
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckBox
                            checked={selected}
                            id=""
                            name=""
                            color="primary"
                          />
                          {connectedToOptions[index]}
                        </Box>
                      );
                    }}
                    noOptionsText="No Connected to Found"
                  />
                }
              />
              <ReportCreateNewLabelField
                label={'Status'}
                component={
                  <Dropdown
                    label={taskBoardConstants.RISK_STATUS}
                    dropDownOptions={taskBoardConstants.STATUS_OPTIONS}
                    dropDownValues={taskBoardConstants.STATUS_VALUES}
                    value={data.activity_status}
                    setValue={(value: string) =>
                      setData((prevData: any) => ({
                        ...prevData,
                        primary_category: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) => {
                      setData((prevData: any) => ({
                        ...prevData,
                        activity_status: event.target.value,
                      }));
                    }}
                    error={!data.primary_category}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={taskBoardConstants.OWNER}
                component={
                  <AutocompleteWithCheckBoxes
                    options={ownerOptions}
                    defaultValue={data.owner}
                    handleChange={(_, value) => {
                      setData((prev: any) => ({
                        ...prev,
                        owner: value,
                      }));
                    }}
                    typable
                    getOptionLabel={(option: any) => option}
                    placeholder={taskBoardConstants.OWNER}
                    width="283px"
                    renderOption={(option: any, { selected }: any) => {
                      return (
                        <Box
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <CheckBox
                            checked={selected}
                            id=""
                            name=""
                            color="primary"
                          />
                          {getTextWithToolTip(option, 30)}
                        </Box>
                      );
                    }}
                    noOptionsText="No Owner Found"
                  />
                }
                marginBottom="12px"
              />

              <ReportCreateNewLabelField
                label={taskBoardConstants.TRACKER}
                component={
                  <Dropdown
                    label={taskBoardConstants.TRACKER}
                    dropDownOptions={trackerOptions}
                    dropDownValues={trackerValues}
                    value={data.tracker}
                    setValue={(value: string) => {
                      setData((prev: any) => ({
                        ...prev,
                        tracker: value,
                      }));
                    }}
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) => {
                      setData((prev: any) => ({
                        ...prev,
                        tracker: event.target.value,
                      }));
                    }}
                  />
                }
              />
              <ReportCreateNewLabelField
                label={taskBoardConstants.Source}
                component={
                  <Dropdown
                    label={taskBoardConstants.Source}
                    dropDownOptions={sourceOptions}
                    dropDownValues={sourceValues}
                    value={data.risk_source}
                    setValue={(value: string) =>
                      setData((prevData: any) => ({
                        ...prevData,
                        risk_source: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) => {
                      const contract_id =
                        contracts?.find(
                          (item: any) => item.name === event.target.value,
                        )?.contract_id ?? '';
                      setData((prevData: any) => ({
                        ...prevData,
                        risk_source: event.target.value,
                        contract_id: contract_id,
                      }));
                    }}
                  />
                }
              />
              {contracts.find(
                (item: any) => item.name === data.risk_source,
              ) && (
                  <ReportCreateNewLabelField
                    label={taskBoardConstants.SOURCE_REPORT}
                    component={
                      <Dropdown
                        label={taskBoardConstants.SOURCE_REPORT}
                        dropDownOptions={
                          contracts.find(
                            (item: any) => item.name === data.risk_source,
                          )?.reports || []
                        }
                        dropDownValues={
                          contracts.find(
                            (item: any) => item.name === data.risk_source,
                          )?.reports || []
                        }
                        value={data.source_report}
                        setValue={(value: string) =>
                          setData((prevData: any) => ({
                            ...prevData,
                            source_report: value,
                          }))
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '283px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          handleChange(event, 'source_report')
                        }
                      />
                    }
                  />
                )}
              {data.activity_status !== 'Suggested' && (
                <ReportCreateNewLabelField
                  label={taskBoardConstants.START_DATE}
                  component={
                    <DatePicker
                      width="283px"
                      handleChange={(value) => {
                        const start_date = moment(value).format('MMMM D, YYYY');
                        setData((prev: any) => ({
                          ...prev,
                          start_date: start_date,
                        }));
                      }}
                      selectedDate={data.start_date}
                      minDate={'Jan 01, 2020'}
                      maxDate={moment().add(1, 'years')}
                      format={'MMMM d, yyyy'}
                      placeholder={taskBoardConstants.DATE_FORMAT}
                      error={!data.start_date}
                    />
                  }
                />
              )}
              {data.activity_status === 'Suggested' && (
                <ReportCreateNewLabelField
                  label={taskBoardConstants.SUGGESTED_START_DATE}
                  component={
                    <DatePicker
                      width="283px"
                      handleChange={(value) => {
                        const suggested_start_date = moment(value).format(
                          'MMMM D, YYYY',
                        );
                        setData((prev: any) => ({
                          ...prev,
                          suggested_start_date: suggested_start_date,
                        }));
                      }}
                      selectedDate={data.suggested_start_date}
                      minDate={'Jan 01, 2020'}
                      maxDate={moment().add(1, 'years')}
                      format={'MMMM d, yyyy'}
                      placeholder={taskBoardConstants.DATE_FORMAT}
                      error={!data.suggested_start_date}
                    />
                  }
                />
              )}
            </Grid>

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {taskBoardConstants.CANCEL}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {taskBoardConstants.ADD_TASK}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  const cleanedData = removeEmptyFields(data);
                  handleSubmit({
                    ...cleanedData,
                  });
                  setOpen(false);
                }}
                disabled={isAddActivityDisabled}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default AddActivityModal;
